import React, { useEffect, useState } from 'react';
import { CheckIcon, LockIcon, ShieldCheckIcon, ZapIcon } from 'lucide-react';
import { GuaranteeSeal } from '../ui/GuaranteeSeal';
import { Section } from '../ui/Section';
import { Reveal } from '../ui/Reveal';
import { BuyButton } from '../ui/BuyButton';
import { PaymentNote } from '../ui/PaymentNote';
import { capitalizeFirst, getTodayLongDate } from '../../utils/localDate';
import { CHECKOUT_URL } from '../../data/content';
interface PricingProps {
  showUrgency?: boolean;
}
const included: {
  item: string;
  value: string;
}[] = [{
  item: 'El Ebook Maestro: 60 páginas de ciencia aplicada',
  value: '$49'
}, {
  item: 'El Mapa de 21 Días, día por día',
  value: '$39'
}, {
  item: 'El Código 1-2-3 de secuenciación',
  value: '$29'
}, {
  item: 'Protocolos de vida real: restaurantes, alcohol y viajes',
  value: '$25'
}, {
  item: 'Biohacking femenino: ciclo menstrual + truco del vinagre',
  value: '$25'
}, {
  item: 'Checklist de freno de emergencia + guía visual',
  value: '$20'
}];
const assurances = [{
  icon: ShieldCheckIcon,
  label: 'Garantía 60 días'
}, {
  icon: ZapIcon,
  label: 'Acceso inmediato'
}, {
  icon: LockIcon,
  label: 'SSL 256-bit'
}];
export function Pricing({
  showUrgency = true
}: PricingProps) {
  const [today, setToday] = useState('');
  useEffect(() => {
    setToday(capitalizeFirst(getTodayLongDate()));
  }, []);
  return <Section id="comprar" className="bg-white" width="narrow" aria-labelledby="inversion-titulo" data-id="element-224" data-mp="1ua4b0k94ho1a">
      <Reveal data-id="element-225" data-mp="1p9zwgzrrz3dp">
        <h2 id="inversion-titulo" className="text-center text-[1.75rem] font-extrabold tracking-tight text-forest sm:text-4xl" data-id="element-226" data-mp="1pbkq8n69miai">
          Inversión
        </h2>
      </Reveal>

      <Reveal delay={0.06} data-id="element-227" data-mp="1gacr62vxddfd">
        <div className="mt-8 overflow-hidden rounded-3xl border border-gold/60 bg-white shadow-lift" data-id="element-228" data-mp="1g91n01yki3nq">
          {showUrgency && <div className="border-b-2 border-alert bg-alert-wash px-5 py-4 text-center" data-id="element-229" data-mp="9v2dsxzx7wep">
              <p className="text-[0.7rem] font-extrabold uppercase tracking-[0.14em] text-alert-deep sm:text-xs" data-id="element-230" data-mp="4t39nlwcwvgp">
                Descuento único hoy
              </p>
              <p className="mt-1 text-sm font-semibold text-alert-deep sm:text-base" data-id="element-231" data-mp="bb6e9yjgaoaw">
                {today || 'Solo por hoy'}
              </p>
            </div>}

          <div className="bg-cream px-5 py-10 text-center sm:px-10" data-id="element-232" data-mp="3tivshnbyhnvn">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted" data-id="element-233" data-mp="1b5s84u75u30">
              Método Glucosa Estable — completo
            </p>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2" data-id="element-234" data-mp="3ohyezo1w5i0h">
              <p className="text-[0.7rem] font-semibold uppercase tracking-[0.14em] text-muted" data-id="element-235" data-mp="39sts3tk5ipm1">
                Precio regular
              </p>
              <p className="relative text-4xl font-extrabold tracking-tight text-faint sm:text-5xl" data-id="element-236" data-mp="3es6eyeb0vwom">
                <span className="line-through decoration-alert decoration-[3px]" data-id="element-237" data-mp="30sdqyqqvrukc">$39 USD</span>
              </p>
              <span className="rounded-md bg-alert px-3 py-1.5 text-lg font-extrabold leading-none text-white sm:text-xl" data-id="element-238" data-mp="35t1iremxoffm">
                −59%
              </span>
            </div>

            <div className="mt-6 flex items-end justify-center gap-2" data-id="element-239" data-mp="2h7n3pqo9ctw5">
              <span className="text-[3.75rem] font-extrabold leading-none tracking-tight text-gold sm:text-8xl" data-id="element-240" data-mp="2v63gnzcprgmq">
                $15,90
              </span>
              <span className="pb-2 text-base font-bold text-gold sm:pb-4 sm:text-2xl" data-id="element-241" data-mp="2r6mcd990wpj5">USD</span>
            </div>

            <p className="mt-5 inline-flex rounded-full bg-forest px-4 py-1.5 text-xs font-bold uppercase tracking-[0.1em] text-white" data-id="element-242" data-mp="3057r0tbdjken">
              Pago único · acceso al método
            </p>

            <p className="mt-5 text-base font-extrabold text-success sm:text-lg" data-id="element-243" data-mp="39uzp3xiomvnv">
              Hoy ahorras $23,10 USD
            </p>
            <p className="mt-2 text-xs text-muted" data-id="element-244" data-mp="3nvk7wtolkrhd">
              Sin suscripciones, sin renovaciones automáticas, sin cargos ocultos.
            </p>
          </div>

          <div className="px-5 pb-10 pt-8 sm:px-10" data-id="element-245" data-mp="1h6i6rydwxnyx">
            <p className="mx-auto max-w-md text-center text-xs font-bold uppercase tracking-[0.14em] text-muted" data-id="element-246" data-mp="1h5qlbge156md">
              Todo lo que recibes hoy
            </p>

            <ul className="mx-auto mt-5 max-w-md divide-y divide-mist border-y border-mist" data-id="element-247" data-mp="1qvth1ocjcyoe">
              {included.map(entry => <li key={entry.item} className="flex items-start gap-3 py-3" data-id="element-248" data-mp="1uwddeppaby9h">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-forest" aria-hidden="true" data-id="element-249" data-mp="y85zyjah4q6i">
                    <CheckIcon className="h-3 w-3 text-white" strokeWidth={3} data-id="element-250" data-mp="136qy8d660zsb" />
                  </span>
                  <span className="text-left text-sm leading-relaxed text-ink" data-id="element-251" data-mp="17a6u37aeuw3y">{entry.item}</span>
                  <span className="ml-auto shrink-0 text-sm font-semibold text-faint line-through" data-id="element-252" data-mp="1c8gnbcckrmmx">
                    {entry.value}
                  </span>
                </li>)}
            </ul>

            <div className="mx-auto mt-5 max-w-md rounded-xl bg-cream px-5 py-4" data-id="element-253" data-mp="2nkhknfni8jea">
              <p className="flex items-center justify-between text-sm font-bold text-ink" data-id="element-254" data-mp="2xpr7nb3uus0r">
                Valor real del paquete
                <span className="text-lg text-faint line-through" data-id="element-255" data-mp="2stim9fz2e2l5">$187 USD</span>
              </p>
              <p className="mt-2 flex items-center justify-between text-sm font-bold text-forest" data-id="element-256" data-mp="2ny3actkmagrd">
                Tu inversión hoy
                <span className="text-2xl font-extrabold text-gold" data-id="element-257" data-mp="2f0aunoazh73g">$15,90 USD</span>
              </p>
            </div>

            <div className="mx-auto mt-8 flex max-w-md flex-col items-center gap-4 rounded-2xl bg-forest px-5 py-6 text-center sm:flex-row sm:text-left" data-id="element-258" data-mp="29zn22y73wi74">
              <GuaranteeSeal className="h-20 w-20 shrink-0" data-id="element-259" data-mp="210028gu0zkg6" />
              <div data-id="element-260" data-mp="211kycavznbg5">
                <p className="text-sm font-extrabold uppercase tracking-[0.1em] text-gold" data-id="element-261" data-mp="21234bz8gd419">
                  Compras con riesgo cero
                </p>
                <p className="mt-2 text-sm leading-relaxed text-white" data-id="element-262" data-mp="3vf2mo047wcc8">
                  60 días completos para probarlo. Si no ves los cambios, tu dinero regresa
                  completo — un botón, reembolso en 48 horas, sin letra chica.
                </p>
              </div>
            </div>

            <div className="mx-auto mt-8 max-w-md" data-id="element-263" data-mp="1jrpef1umhcf9">
              <BuyButton href={CHECKOUT_URL} data-id="element-264" data-mp="1jout783sbnks">Sí, Quiero El Protocolo Completo</BuyButton>
            </div>

            <p className="mt-4 flex items-center justify-center gap-2 text-center text-xs font-medium text-muted" data-id="element-265" data-mp="2bhu51i8z6vox">
              <LockIcon className="h-3.5 w-3.5 shrink-0 text-forest" aria-hidden="true" data-id="element-266" data-mp="2ghgk7x8d6m0d" />
              Procesado por Hotmart. Seguridad certificada SSL 256-bit.
            </p>

            <PaymentNote className="mx-auto mt-3 max-w-md" data-id="element-267" data-mp="27g8l5cy0xh58" />

            <ul className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 border-t border-gold/30 pt-6" data-id="element-268" data-mp="27ep61po3tc51">
              {assurances.map(({
              icon: Icon,
              label
            }) => <li key={label} className="flex items-center gap-2 text-xs font-medium text-muted" data-id="element-269" data-mp="w2el25flqkb5">
                  <Icon className="h-4 w-4 text-forest" aria-hidden="true" data-id="element-270" data-mp="ni9mlkdr9co9" />
                  {label}
                </li>)}
            </ul>

            {showUrgency && <p className="mt-6 text-center text-xs text-faint" data-id="element-271" data-mp="xiimb86yhg8e">
                Después de hoy, el precio vuelve a $39 USD.
              </p>}
          </div>
        </div>
      </Reveal>
    </Section>;
}